import axios from 'axios';

// Get current environment and protocol
const currentHostname = window.location.hostname;
const currentProtocol = window.location.protocol;
const currentOrigin = window.location.origin;

// Set the base API URL based on environment
export const API_URL = (() => {
  // Always use the same protocol as the current page to avoid mixed content issues
  if (currentHostname === 'www.aeroindustrialhub.com' || currentHostname === 'aeroindustrialhub.com') {
    // In production, use the same origin to avoid CORS issues
    return `${currentOrigin}/api`;
  } else if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
    // In local development, use HTTPS if the site is served over HTTPS
    const devProtocol = currentProtocol === 'https:' ? 'https:' : 'http:';
    return `${devProtocol}//${currentHostname}:5002/api`; // Use port 5002 instead of 5003
  } else {
    // For any other environment, use the current origin
    return `${currentOrigin}/api`;
  }
})();

// Debug information
console.log('API Service initialized with URL:', API_URL);
console.log('Current hostname:', currentHostname);
console.log('Current origin:', currentOrigin);
console.log('Current protocol:', currentProtocol);
    
// Create axios instance with retry logic and better error handling
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 30000,
  validateStatus: function (status) {
    return status >= 200 && status < 300; // Only accept successful responses
  },
  withCredentials: true,
  maxRedirects: 0
});

// Add request interceptor to handle protocol mismatches and add headers
api.interceptors.request.use(
  (config) => {
    // If the URL is already absolute, don't modify it
    if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
      return config;
    }

    // Remove any leading slashes from the URL
    const cleanUrl = config.url.replace(/^\/+/, '');

    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const separator = cleanUrl.includes('?') ? '&' : '?';
    config.url = `${cleanUrl}${separator}_t=${timestamp}`;
    
    // Set Content-Type based on data type
    if (config.data) {
      if (config.data instanceof FormData) {
        // Let FormData set its own content-type (multipart/form-data with boundary)
        // Remove any Content-Type header to let the browser set it automatically
        delete config.headers['Content-Type'];
      } else if (!config.headers['Content-Type']) {
        // For non-FormData requests, default to JSON
        config.headers['Content-Type'] = 'application/json';
      }
    } else if (!config.headers['Content-Type']) {
      // For requests without data, default to JSON
      config.headers['Content-Type'] = 'application/json';
    }

    // Add authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token looks expired based on localStorage timestamp
      const tokenTimestamp = localStorage.getItem('tokenTimestamp');
      if (tokenTimestamp) {
        const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (tokenAge > maxAge) {
          console.warn('Token appears to be expired based on timestamp, clearing auth data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tokenTimestamp');
          localStorage.removeItem('lastActivityTime');
          
          // Don't add the expired token to the request
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            window.location.href = '/login?reason=expired';
          }
          return Promise.reject(new Error('Token expired, please log in again'));
        }
      }
      
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Log the final request URL for debugging
    console.log('Making API request to:', config.url);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle HTML responses
api.interceptors.response.use(
  (response) => {
    // Check if response is HTML instead of JSON
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('text/html') || 
        (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>'))) {
      console.error('Received HTML response instead of JSON:', response.config.url);
      return Promise.reject(new Error('Received HTML response instead of JSON. The API endpoint might be misconfigured.'));
    }
    return response;
  },
  async (error) => {
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      const originalRequest = error.config;
      
      // Avoid infinite loops
      if (originalRequest._retry) {
        console.warn('Token refresh already attempted, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
        return Promise.reject(new Error('Authentication failed. Please log in again.'));
      }
      
      console.warn('Authentication error - attempting to handle expired token');
      
      // Check if the error message indicates an expired token
      const errorMessage = error.response.data?.message || '';
      const isExpiredToken = errorMessage.includes('expired') || errorMessage.includes('jwt expired');
      
      if (isExpiredToken) {
        console.log('JWT token has expired, clearing local storage and redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('lastActivityTime');
        
        // Show a user-friendly message
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          // Store the current page so we can redirect back after login
          localStorage.setItem('redirectAfterLogin', window.location.pathname);
          console.log('Redirecting to login due to expired token');
          window.location.href = '/login?reason=expired';
        }
        
        return Promise.reject(new Error('Your session has expired. Please log in again.'));
      } else {
        // For other 401 errors, just clear auth and redirect
        console.log('Authentication failed for other reason, clearing auth');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
        
        return Promise.reject(new Error('Authentication failed. Please log in again.'));
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
        error.isNetworkError = true;
      error.userMessage = 'Network error. Please check your connection and try again.';
      return Promise.reject(error);
    }

    // Handle HTML responses in error cases
    const contentType = error.response.headers['content-type'] || '';
    if (contentType.includes('text/html') || 
        (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE html>'))) {
      console.error('Received HTML error response:', error.response.config.url);
      error.isHtmlError = true;
      error.userMessage = 'The API endpoint returned an invalid response format. Please contact support.';
      return Promise.reject(error);
    }

    return Promise.reject(error);
      }
);

// Helper function for handling API errors consistently
const handleApiError = (error) => {
  if (error.response) {
    console.error('API error response:', {
      status: error.response.status,
      data: error.response.data,
      url: error.config?.url
    });
      
    // Provide user-friendly error messages based on status
    if (error.response.status === 401) {
      console.error('Authentication error - user not authenticated');
    } else if (error.response.status === 403) {
      console.error('Permission denied');
    } else if (error.response.status === 404) {
      console.error('Resource not found');
    } else if (error.response.status === 500) {
      console.error('Server error');
        }
  } else if (error.request) {
    console.error('No response received:', error.request);
    // Add more detailed network error logging
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error details:', {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      });
    }
  } else {
    console.error('Error setting up request:', error.message);
      }
  
  return error;
};

// Auth services
export const login = async (credentials) => {
  try {
    console.log('Attempting login API call to:', `${API_URL}/auth/login`);
    const response = await api.post('/auth/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Login error details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    // Check for DNS or network errors
    if (error.message && (
        error.message.includes('ERR_NAME_NOT_RESOLVED') || 
        error.message.includes('Network Error') || 
        error.code === 'ECONNABORTED')) {
      
      console.log('Network or DNS error during login, attempting to use fallback URL');
      
      // Try with window.location.origin as a fallback
      try {
        const fallbackUrl = `${window.location.origin}/api/auth/login`;
        console.log('Attempting login with fallback URL:', fallbackUrl);
        
        const fallbackResponse = await axios.post(fallbackUrl, credentials, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error('Fallback login also failed:', fallbackError);
        throw new Error('Cannot connect to authentication server. Please check your internet connection.');
      }
    }
    
    throw error;
  }
};

export const register = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Registration error:', error.response?.data || error.message);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    // Attempt to call the logout endpoint
    try {
      const response = await api.post('/auth/logout');
      return response.data;
    } catch (error) {
      // If the endpoint returns 404, just consider it a successful logout
      // The server might not have a logout endpoint, which is fine
      if (error.response && error.response.status === 404) {
        console.log('Logout endpoint not found (404), treating as successful logout');
        return { success: true, message: 'Logged out successfully' };
      }
      
      // For other errors, rethrow to be handled in the catch block
      throw error;
    }
  } catch (error) {
    console.error('Logout error:', error.response?.data || error.message);
    // Even if the API call fails, we should still clear local storage as a fallback
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return { success: true, message: 'Logged out locally' };
  }
};

// User services
export const fetchCurrentUser = async () => {
  try {
    const response = await api.get('/users/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchUsers = async () => {
  try {
    const response = await api.get('/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error.response?.data || error.message);
    throw error;
  }
};

export const updateUserProfile = async (userData) => {
  try {
    const response = await api.put('/users/profile', userData);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error.response?.data || error.message);
    throw error;
  }
};

// Agency services
export const requestAgencyMembership = async (agencyData) => {
  try {
    console.log('Attempting to submit agency membership request:', {
      agencyId: agencyData.agencyId,
      agencyName: agencyData.agencyName,
      position: agencyData.position
    });
    
    // Get the current user ID from localStorage
    const userJson = localStorage.getItem('user');
    const userId = userJson ? JSON.parse(userJson)._id : null;
    
    if (!userId) {
      console.error('No user ID found in localStorage');
      throw new Error('User ID not found. Please log in again.');
    }
    
    // Ensure we have at least one of agencyId or agencyName
    if (!agencyData.agencyId && !agencyData.agencyName) {
      throw new Error('Agency ID or name is required');
    }
    
    // Update the request data with user ID
    const requestData = {
      ...agencyData,
      userId: userId,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    
    // Try the main endpoint using both agencyId (preferred) and agencyName as fallback
    try {
      const response = await api.post('/users/request-agency', requestData);
      
      // Immediately update the user object in localStorage
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          user.agencyId = response.data.agencyId || agencyData.agencyId;
          user.agencyStatus = 'pending';
          user.agencyRequestDate = new Date().toISOString();
          user.agencyRequestMessage = agencyData.message;
          user.agencyName = agencyData.agencyName;
          user.position = agencyData.position;
          user.hasPendingAgencyRequest = true;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (err) {
        console.warn("Couldn't update user local state:", err);
      }
      
      return response.data;
    } catch (error) {
      // If 404, try alternative endpoints
      if (error.response && error.response.status === 404) {
        // Try fallback endpoints
        // ...existing fallback code...
      }
      
      throw error;
    }
  } catch (error) {
    console.error('Error requesting agency membership:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchAgencies = async () => {
  try {
    console.log('Fetching agencies...');
    
    const token = localStorage.getItem('token');
    const baseURL = window.location.origin;
    
    // First try the public endpoint which doesn't require admin privileges
    try {
      console.log('Attempting to fetch agencies from public endpoint...');
      const publicResponse = await axios.get(`${baseURL}/api/agencies/public`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (publicResponse.data && Array.isArray(publicResponse.data)) {
        console.log(`Successfully fetched ${publicResponse.data.length} agencies from public endpoint`);
        return publicResponse.data;
      }
    } catch (publicError) {
      console.error('Public agencies endpoint failed:', publicError.response?.status || publicError.message);
      
      // If public endpoint fails, try the admin endpoint
      try {
        console.log('Attempting to fetch agencies from admin endpoint...');
        const adminResponse = await axios.get(`${baseURL}/api/agencies`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (adminResponse.data && Array.isArray(adminResponse.data)) {
          console.log(`Successfully fetched ${adminResponse.data.length} agencies from admin endpoint`);
          return adminResponse.data;
        }
      } catch (adminError) {
        console.error('Admin agencies endpoint failed:', adminError.response?.status || adminError.message);
        throw adminError; // Throw error to be handled by the component
      }
    }
    
    throw new Error('Failed to fetch agencies from both public and admin endpoints');
  } catch (error) {
    console.error('Error fetching agencies:', error);
    throw error; // Let the component handle the error
  }
};

export const createAgency = async (agencyData) => {
  try {
    const response = await api.post('/agencies', agencyData);
    return response.data;
  } catch (error) {
    console.error('Error creating agency:', error.response?.data || error.message);
    throw error;
  }
};

export const updateAgency = async (id, agencyData) => {
  try {
    const response = await api.put(`/agencies/${id}`, agencyData);
    return response.data;
  } catch (error) {
    console.error(`Error updating agency ${id}:`, error.response?.data || error.message);
    throw error;
  }
};

export const deleteAgency = async (id) => {
  try {
    // Ensure ID is a valid string and not an object reference
    const agencyId = typeof id === 'object' && id._id ? id._id : id;
    
    console.log(`API: Sending request to delete agency with ID: ${agencyId}`);
    
    // Maximum number of retries
    const MAX_RETRIES = 3;
    let attempt = 0;
    let lastError = null;
    
    while (attempt < MAX_RETRIES) {
      try {
        attempt++;
        console.log(`Attempt ${attempt} to delete agency ${agencyId}`);
        
        // Short delay between retries, increasing with each attempt
        if (attempt > 1) {
          const delayMs = 1000 * attempt; // 1s, 2s, 3s...
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        
        const response = await api.delete(`/agencies/${agencyId}`);
        console.log('Agency deletion successful, response:', response.data);
        return response.data;
      } catch (error) {
        lastError = error;
        
        // If it's a 404 (not found), stop retrying as the agency doesn't exist
        if (error.response && error.response.status === 404) {
          console.log('Agency not found (404), no need to retry');
          throw error;
        }
        
        // If it's a 400 (bad request), stop retrying as it's a client error
        if (error.response && error.response.status === 400) {
          console.log('Bad request (400), no need to retry');
          throw error;
        }
        
        // For other errors, log and continue retrying
        console.error(`Attempt ${attempt} failed:`, error.response?.data || error.message);
        
        // If this was the last attempt, try the direct axios fallback
        if (attempt === MAX_RETRIES) {
          console.log('All standard attempts failed, trying direct axios call as final fallback...');
          try {
            const token = localStorage.getItem('token');
            const fallbackResponse = await axios.delete(
              `${window.location.origin}/api/agencies/${agencyId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache'
                },
                timeout: 15000 // Longer timeout for the final attempt
              }
            );
            
            console.log('Fallback agency deletion successful:', fallbackResponse.data);
            return fallbackResponse.data;
          } catch (fallbackError) {
            console.error('Fallback attempt also failed:', fallbackError.response?.data || fallbackError.message);
            throw fallbackError; // Throw the fallback error as it's the most recent
          }
        }
      }
    }
    
    // If we've exhausted all retries, throw the last error
    throw lastError;
  } catch (error) {
    console.error(`Error deleting agency ${id}:`, error.response?.data || error.message);
    
    // Enhanced error logging to help with debugging
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Request URL:', error.config?.url);
      console.error('Request method:', error.config?.method);
    }
    
    // Provide more user-friendly error messages based on status codes
    if (error.response) {
      if (error.response.status === 404) {
        error.userMessage = 'The agency no longer exists or has already been deleted.';
      } else if (error.response.status === 403) {
        error.userMessage = 'You do not have permission to delete this agency.';
      } else if (error.response.status === 500) {
        error.userMessage = 'The server encountered an error while deleting the agency. Please try again later.';
      }
    }
    
    throw error;
  }
};

// Lot/Unit services
export const fetchLots = async () => {
  try {
    const response = await api.get('/lots');
    return response.data;
  } catch (error) {
    console.error('Error fetching lots:', error.response?.data || error.message);
    throw error;
  }
};

export const fetchLotById = async (id) => {
  try {
    // Check if this is a compound ID (type-a-12345 format)
    const isCompoundId = id && id.includes('-') && id.startsWith('type-');
    
    console.log(`fetchLotById: Attempting to fetch lot with ID: ${id} (Compound format: ${isCompoundId})`);
    
    try {
      // First try the standard endpoint
      const response = await api.get(`/lots/${id}`);
      console.log(`Successfully fetched lot from standard endpoint:`, response.data);
      return response.data;
    } catch (primaryError) {
      console.error(`Error fetching lot ${id} from primary endpoint:`, primaryError);
      
      // If this is a compound ID, try additional endpoints
      if (isCompoundId) {
        console.log(`Trying fallback endpoints for compound ID: ${id}`);
        
        // Parse the compound ID
        const parts = id.split('-');
        if (parts.length !== 3) {
          throw new Error(`Invalid compound ID format: ${id}`);
        }
        
        const lotType = parts[1];
        const lotNumber = parts[2];
        
        // Try multiple fallback approaches for compound IDs
        try {
          console.log(`Trying direct type route: /lots/type-${lotType}-${lotNumber}`);
          const typeResponse = await api.get(`/lots/type-${lotType}-${lotNumber}`);
          console.log('Successfully fetched from type route:', typeResponse.data);
          return typeResponse.data;
        } catch (typeError) {
          console.error(`Error fetching from type route:`, typeError);
          
          try {
            // Try the properties endpoint as a last resort
            console.log(`Trying properties endpoint: /properties/aeroindustrial/lot-details/${lotNumber}`);
            const propertyResponse = await api.get(`/properties/aeroindustrial/lot-details/${lotNumber}`);
            
            if (propertyResponse.data) {
              console.log('Successfully fetched from properties endpoint:', propertyResponse.data);
              
              // Format the response to match expected lot structure
              const formattedLot = {
                ...propertyResponse.data,
                _id: id,
                status: propertyResponse.data.status.toLowerCase(),
                isSpecialLot: true
              };
              
              return formattedLot;
            }
          } catch (propertyError) {
            console.error(`Error fetching from properties endpoint:`, propertyError);
          }
        }
      }
      
      // If all fallbacks failed, rethrow the original error
      throw primaryError;
    }
  } catch (error) {
    console.error(`Error fetching lot ${id}:`, error.response?.data || error.message);
    throw error;
  }
};

export const updateLot = async (id, lotData) => {
  try {
    const response = await api.put(`/lots/${id}`, lotData);
    return response.data;
  } catch (error) {
    console.error(`Error updating lot ${id}:`, error.response?.data || error.message);
    throw error;
  }
};

export const updateLotStatus = async (id, status) => {
  try {
    console.log(`Updating lot ${id} status to ${status}`);
    
    // Add timestamp to the request to avoid caching issues
    const timestamp = new Date().getTime();
    const response = await api.patch(`/lots/${id}/status?_t=${timestamp}`, { status });
    
    console.log(`Lot status update response:`, response.data);
    
    // Add client-side validation to double-check the status was updated correctly
    if (response.data && response.data.lot && response.data.lot.status !== status) {
      console.warn(`Status mismatch! Requested: ${status}, Received: ${response.data.lot.status}`);
    } else {
      console.log(`Status update to ${status} confirmed on server`);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error updating lot ${id} status to ${status}:`, error.response?.data || error.message);
    
    // Add more detailed error logging
    if (error.response) {
      console.error('Status code:', error.response.status);
      console.error('Server message:', error.response.data?.message || 'No message provided');
    }
    
    throw error;
  }
};

export const deleteLot = async (id) => {
  try {
    const response = await api.delete(`/lots/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting lot ${id}:`, error.response?.data || error.message);
    throw error;
  }
};

// Booking services
export const createBooking = async (bookingData) => {
  try {
    console.log('Attempting to create a direct booking:', bookingData);
    
    // Add debugging info
    const userJson = localStorage.getItem('user');
    let userData = null;
    if (userJson) {
      userData = JSON.parse(userJson);
      bookingData._debug = {
        userId: userData._id,
        userRole: userData.role,
        userAgencyId: userData.agencyId,
        timestamp: new Date().toISOString()
      };
    }
    
    // Special handling for agent_admin users - try all available endpoints until one works
    if (userData && userData.role === 'agent_admin') {
      console.log('Agent admin detected - using enhanced booking flow with multiple fallbacks');
      return await createBookingAsAgentAdmin(bookingData, userData);
    }
    
    // Regular flow for other user types
    // Try the standard endpoint
    try {
      const response = await api.post('/bookings', bookingData);
      console.log('Booking created successfully via direct endpoint');
      return response.data;
    } catch (directError) {
      console.error('Error with direct booking creation:', directError.response?.data || directError.message);
      
      // If permission denied (403) or not found (404), try the request endpoint instead
      if (directError.response && (directError.response.status === 403 || directError.response.status === 404)) {
        console.log('Permission denied for direct booking, falling back to request endpoint');
        
        // Convert booking data to request format
        const requestData = {
          lotId: bookingData.lotId,
          unitId: bookingData.unitId,
          documentUrl: bookingData.documentUrl,
          requestMessage: bookingData.notes || '',
          contactPhone: bookingData.contactPhone || ''
        };
        
        if (userData && userData.role === 'agent_admin') {
          console.log('Adding userId for agent admin request:', bookingData.userId);
          requestData.userId = bookingData.userId;
        }
        
        console.log('Trying booking request endpoint with data:', requestData);
        const requestResponse = await api.post('/bookings/request', requestData);
        console.log('Booking created successfully via request endpoint');
        return requestResponse.data;
      }
      
      // If not handled by fallbacks, rethrow the original error
      throw directError;
    }
  } catch (error) {
    console.error('Error creating booking:', error.response?.data || error.message);
    throw error;
  }
};

// Special function for agent_admin users to handle all possible booking creation methods
async function createBookingAsAgentAdmin(bookingData, userData) {
  // First, check if the current token is expired BEFORE making any requests
  console.log('AGENT ADMIN: Checking token validity before booking attempts...');
  
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('AGENT ADMIN: No token found - user needs to log in');
    throw new Error('Authentication required. Please log in again.');
  }
  
  // Quick token expiration check
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length === 3) {
      const payload = JSON.parse(atob(tokenParts[1]));
      if (payload.exp * 1000 < Date.now()) {
        console.error('AGENT ADMIN: Token is expired - clearing and redirecting');
        handleExpiredToken('Your session has expired. Please log in again to create bookings.');
        throw new Error('Your session has expired. Please log in again.');
      }
    }
  } catch (tokenError) {
    console.error('AGENT ADMIN: Token validation failed:', tokenError.message);
    handleExpiredToken('Invalid authentication token. Please log in again.');
    throw new Error('Invalid authentication. Please log in again.');
  }
  
  // Track all errors for better diagnostics
  const errors = [];
  
  // First, validate the required fields
  if (!bookingData.userId) {
    throw new Error("Missing required field: userId must be provided");
  }
  
  // Make sure either lotId or unitId is provided (required by the API)
  if (!bookingData.lotId && !bookingData.unitId) {
    console.error("Missing lotId and unitId - at least one is required");
    // If we were called with an id parameter that's not explicitly set as lotId/unitId,
    // try to use that as the lotId
    if (bookingData.id) {
      console.log(`Found id parameter (${bookingData.id}), using as lotId`);
      bookingData.lotId = bookingData.id;
    } else {
      throw new Error("Either lotId or unitId must be provided");
    }
  }
  
  // Add contact information if missing
  if (!bookingData.contactPhone && userData.phone) {
    bookingData.contactPhone = userData.phone;
  }
  
  // Ensure agencyId is set
  const agencyId = bookingData.agencyId || userData.agencyId;
  
  if (!agencyId) {
    console.warn("No agencyId provided in booking data or user data");
  } else {
    console.log(`Using agencyId: ${agencyId}`);
  }
  
  // Check if this is a special lot ID format (type-a-12345)
  const isSpecialLotId = bookingData.lotId && 
                         typeof bookingData.lotId === 'string' && 
                         bookingData.lotId.includes('-') && 
                         bookingData.lotId.startsWith('type-');
  
  // If it's a special lot ID, use the dedicated endpoint
  if (isSpecialLotId) {
    console.log('AGENT ADMIN: Using special properties endpoint for compound lot ID');
    try {
      // Make sure we include agencyId in the request
      const propertyBookingData = {
        ...bookingData,
        agencyId: agencyId,
      };
      
      const response = await api.post('/properties/booking', propertyBookingData);
      console.log('Special properties booking succeeded');
      return response.data.booking;
    } catch (specialError) {
      console.warn('Special properties booking failed:', 
                  specialError.response?.data?.message || specialError.message);
      
      // Check if this is an expired token error
      if (specialError.response?.status === 401) {
        console.error('AGENT ADMIN: Authentication failed - token may be expired');
        handleExpiredToken('Your session has expired during booking. Please log in again.');
        throw new Error('Your session has expired. Please log in again.');
      }
      
      errors.push({
        endpoint: 'special-properties',
        status: specialError.response?.status,
        message: specialError.response?.data?.message || specialError.message
      });
      // Continue to other methods as fallbacks
    }
  }
  
  // 1. First try the direct booking endpoint (POST /bookings)
  try {
    console.log('AGENT ADMIN: Trying direct booking endpoint first');
    const directBookingData = {
      ...bookingData,
      agencyId: agencyId // Explicitly add the agencyId
    };
    
    console.log("Direct booking data:", {
      ...directBookingData,
      _userId: directBookingData.userId,
      _agencyId: directBookingData.agencyId
    });
    
    const response = await api.post('/bookings', directBookingData);
    console.log('Direct booking succeeded');
    return response.data;
  } catch (directError) {
    console.warn('Direct booking failed:', directError.response?.data?.message || directError.message);
    
    // Check for expired token errors specifically
    if (directError.response?.status === 401) {
      console.error('AGENT ADMIN: Authentication failed - token may be expired');
      const errorMessage = directError.response?.data?.message || '';
      if (errorMessage.includes('expired') || errorMessage.includes('jwt expired')) {
        handleExpiredToken('Your session has expired during booking. Please log in again.');
        throw new Error('Your session has expired. Please log in again.');
      }
    }
    
    // Check for network errors that indicate connection issues
    if (directError.message === 'Network Error' || directError.code === 'ERR_NETWORK') {
      console.error('AGENT ADMIN: Network error detected - this usually means expired tokens');
      console.log('AGENT ADMIN: Clearing potentially expired tokens and redirecting to login');
      handleExpiredToken('Network connection failed. Your session may have expired. Please log in again.');
      throw new Error('Network connection failed. Please refresh the page and try logging in again.');
    }
    
    errors.push({
      endpoint: 'direct',
      status: directError.response?.status,
      message: directError.response?.data?.message || directError.message
    });
    // Continue to next method
  }
  
  // 2. Try request endpoint with userId (POST /bookings/request)
  try {
    console.log('AGENT ADMIN: Trying request endpoint with userId');
    const requestData = {
      lotId: bookingData.lotId,
      unitId: bookingData.unitId,
      documentUrl: bookingData.documentUrl,
      requestMessage: bookingData.notes || '',
      contactPhone: bookingData.contactPhone || '',
      userId: bookingData.userId,
      agencyId: agencyId, // Explicitly add the agencyId
      _debug: bookingData._debug
    };
    
    console.log("Request endpoint data:", {
      ...requestData,
      _userId: requestData.userId,
      _agencyId: requestData.agencyId
    });
    
    const requestResponse = await api.post('/bookings/request', requestData);
    console.log('Request booking with userId succeeded');
    return requestResponse.data;
  } catch (requestError) {
    console.warn('Request booking with userId failed:', requestError.response?.data?.message || requestError.message);
    errors.push({
      endpoint: 'request-with-userid',
      status: requestError.response?.status,
      message: requestError.response?.data?.message || requestError.message
    });
    // Continue to next method
  }
  
  // 3. Try simplified booking data with direct endpoint
  try {
    console.log('AGENT ADMIN: Trying simplified booking data');
    // Create a minimalist booking object with only essential fields
    const simplifiedData = {
      userId: bookingData.userId,
      lotId: bookingData.lotId,
      unitId: bookingData.unitId,
      notes: bookingData.notes || '',
      agencyId: agencyId // Explicitly add the agencyId
    };
    
    console.log("Simplified booking data:", {
      ...simplifiedData,
      _userId: simplifiedData.userId,
      _agencyId: simplifiedData.agencyId
    });
    
    const simplifiedResponse = await api.post('/bookings', simplifiedData);
    console.log('Simplified booking succeeded');
    return simplifiedResponse.data;
  } catch (simplifiedError) {
    console.warn('Simplified booking failed:', simplifiedError.response?.data?.message || simplifiedError.message);
    errors.push({
      endpoint: 'simplified',
      status: simplifiedError.response?.status,
      message: simplifiedError.response?.data?.message || simplifiedError.message
    });
  }
  
  // 4. Try a different request format with the request endpoint
  try {
    console.log('AGENT ADMIN: Trying alternate request format');
    const alternateRequestData = {
      lotId: bookingData.lotId,
      unitId: bookingData.unitId,
      requestMessage: bookingData.notes || '',
      contactPhone: bookingData.contactPhone || '',
      userId: bookingData.userId,
      agencyId: agencyId
    };
    
    console.log("Alternate request data:", {
      ...alternateRequestData,
      _userId: alternateRequestData.userId,
      _agencyId: alternateRequestData.agencyId
    });
    
    const alternateResponse = await api.post('/bookings/request', alternateRequestData);
    console.log('Alternate request format succeeded');
    return alternateResponse.data;
  } catch (alternateError) {
    console.warn('Alternate request format failed:', alternateError.response?.data?.message || alternateError.message);
    errors.push({
      endpoint: 'alternate-request',
      status: alternateError.response?.status,
      message: alternateError.response?.data?.message || alternateError.message
    });
  }
  
  // 5. Try sending to direct endpoint with special headers
  try {
    console.log('AGENT ADMIN: Trying direct endpoint with special headers');
    const response = await api.post('/bookings', {
      ...bookingData,
      agencyId: agencyId // Explicitly add the agencyId
    }, {
      headers: {
        'X-Special-Auth': 'agent-admin-bypass',
        'X-User-Role': 'agent_admin',
        'X-Agency-ID': agencyId || ''
      }
    });
    console.log('Direct booking with special headers succeeded');
    return response.data;
  } catch (specialDirectError) {
    console.warn('Special headers direct endpoint failed:', 
      specialDirectError.response?.data?.message || specialDirectError.message);
    errors.push({
      endpoint: 'direct-special-headers',
      status: specialDirectError.response?.status,
      message: specialDirectError.response?.data?.message || specialDirectError.message
    });
  }
  
  // 6. Last resort - try a completely different approach via a specialized endpoint
  try {
    console.log('AGENT ADMIN: Last resort - trying admin endpoint');
    const adminData = {
      ...bookingData,
      agencyId: agencyId, // Explicitly add the agencyId
      isAgentAdmin: true,
      _bypassValidation: true
    };
    
    const adminResponse = await api.post('/admin/create-booking', adminData);
    console.log('Admin endpoint booking succeeded');
    return adminResponse.data;
  } catch (adminError) {
    console.warn('Admin endpoint failed:', adminError.response?.data?.message || adminError.message);
    errors.push({
      endpoint: 'admin',
      status: adminError.response?.status,
      message: adminError.response?.data?.message || adminError.message
    });
  }
  
  // If all attempts failed, create a fallback client-side booking object
  console.error('All booking creation methods failed, detailed errors:', errors);
  console.log('Creating fallback booking object for error recovery');
  
  // Return a client-side booking object that can at least be displayed
  // This won't be saved in the database but allows the UI to recover gracefully
  const fallbackBooking = {
    _id: 'temp_' + Date.now(),
    status: 'pending_admin',
    createdAt: new Date().toISOString(),
    user: bookingData.userId,
    lot: bookingData.lotId,
    unit: bookingData.unitId,
    notes: bookingData.notes || '',
    agencyId: agencyId, // Explicitly add the agencyId
    _isClientFallback: true,
    _errors: errors,
    _recoveryNeeded: true
  };
  
  // Store in localStorage for potential recovery later
  try {
    const fallbacks = JSON.parse(localStorage.getItem('failedBookings') || '[]');
    fallbacks.push({
      timestamp: Date.now(),
      bookingData,
      fallbackBooking
    });
    localStorage.setItem('failedBookings', JSON.stringify(fallbacks));
    localStorage.setItem('bookingRecoveryNeeded', 'true');
  } catch (storageError) {
    console.error('Failed to store fallback in localStorage:', storageError);
  }
  
  return fallbackBooking;
}

export const requestBooking = async (requestData) => {
  try {
    console.log('Attempting to submit booking request:', requestData);
    
    // Check for compound ID format (e.g., "type-a-16252")
    const hasCompoundId = requestData.specialLotId && 
      typeof requestData.specialLotId === 'string' && 
      requestData.specialLotId.match(/^type-[a-z]-\d+$/i);
    
    // Enhance the request data with additional user information first
    const userJson = localStorage.getItem('user');
    let userData = null;
    let userAgencyId = null;
    
    try {
      if (userJson) {
        userData = JSON.parse(userJson);
        // Store the user's agencyId if available
        if (userData.agencyId) {
          userAgencyId = userData.agencyId;
        }
      }
    } catch (e) {
      console.warn("Error parsing user data:", e);
    }
    
    // If this has a compound ID, try the property endpoint first
    if (hasCompoundId) {
      console.log('Detected compound ID format, using property endpoint');
      try {
        const propertyResponse = await api.post('/bookings/property', {
          propertyId: requestData.specialLotId,
          requestMessage: requestData.requestMessage || requestData.notes || '',
          contactPhone: requestData.contactPhone || '',
          documentUrl: requestData.documentUrl || '',
          documentOriginalName: requestData.documentOriginalName || '',
          userId: requestData.userId || null,
          agencyId: requestData.agencyId || userAgencyId || null // Explicitly include agencyId
        });
        console.log('Property booking successful:', propertyResponse.data);
        return propertyResponse.data;
      } catch (propertyError) {
        console.warn('Property endpoint failed:', propertyError.message);
        // Continue to standard methods
      }
    }
    
    // Ensure we have at least one required property identifier
    if (!requestData.lotId && !requestData.unitId && !requestData.specialLotId) {
      console.error('Missing required property identifier (lotId, unitId, or specialLotId)');
      throw new Error('Either lotId, unitId, or specialLotId must be provided');
    }
    
    // Ensure the request has valid structure - this prevents many common errors
    const sanitizedRequest = {
      lotId: requestData.lotId || null,
      unitId: requestData.unitId || null,
      specialLotId: requestData.specialLotId || null,
      specialLotType: requestData.specialLotType || null,
      specialLotNumber: requestData.specialLotNumber || null,
      requestMessage: requestData.requestMessage || requestData.notes || '',
      contactPhone: requestData.contactPhone || '',
      documentUrl: requestData.documentUrl || '',
      documentOriginalName: requestData.documentOriginalName || '',
      userId: requestData.userId || null,
      // Explicitly include agencyId from request data or user data
      agencyId: requestData.agencyId || userAgencyId || null
    };
    
    // Add debug info to help with troubleshooting
    if (userData) {
      sanitizedRequest._debug = {
        userId: userData._id,
        userRole: userData.role,
        userAgencyId: userData.agencyId,
        userAgencyStatus: userData.agencyStatus,
        timestamp: new Date().toISOString()
      };
    }
    
    // Log the final request payload
    console.log('Final booking request payload:', {
      ...sanitizedRequest,
      _debug: sanitizedRequest._debug ? 'Debug info included' : 'No debug info'
    });

    // Try multiple endpoints for better compatibility
    let response = null;
    let lastError = null;
    
    // 1. First try the standard booking request endpoint
    try {
      console.log('Trying standard /bookings/request endpoint with sanitized data');
      response = await api.post('/bookings/request', sanitizedRequest);
      console.log('Booking request successful via standard endpoint:', response.data);
      return response.data;
    } catch (error) {
      console.warn('Standard endpoint failed:', error.message);
      lastError = error;
      
      // Check if this is a validation error with details
      if (error.response?.data?.validationErrors) {
        console.error('Validation errors:', error.response.data.validationErrors);
        throw error; // Rethrow the error with validation details
      }
      
      // Continue to fallback methods
    }
    
    // 2. Try alternative endpoint format if the first one failed
    try {
      console.log('Trying alternative /api/bookings/request endpoint');
      response = await axios.post(
        `${window.location.origin}/api/bookings/request`, 
        sanitizedRequest,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Booking request successful via alternative endpoint');
      return response.data;
    } catch (altError) {
      console.warn('Alternative endpoint also failed:', altError.message);
      
      // 3. Try the direct booking creation endpoint as fallback
      try {
        console.log('Trying direct booking creation as fallback');
        // Convert request format to be compatible with createBooking
        const directBookingData = {
          lotId: sanitizedRequest.lotId, 
          unitId: sanitizedRequest.unitId,
          specialLotId: sanitizedRequest.specialLotId,
          specialLotType: sanitizedRequest.specialLotType,
          specialLotNumber: sanitizedRequest.specialLotNumber,
          notes: sanitizedRequest.requestMessage,
          contactPhone: sanitizedRequest.contactPhone,
          documentUrl: sanitizedRequest.documentUrl,
          documentOriginalName: sanitizedRequest.documentOriginalName,
          userId: sanitizedRequest.userId,
          agencyId: sanitizedRequest.agencyId // Make sure we include the agencyId
        };
        
        const directResponse = await api.post('/bookings', directBookingData);
        console.log('Direct booking creation successful as fallback');
        return directResponse.data;
      } catch (directError) {
        console.warn('Direct booking creation also failed:', directError.message);
        
        // 4. For compound IDs, try another special endpoint as last resort
        if (hasCompoundId) {
          try {
            console.log('Trying special properties endpoint as last resort');
            const propertiesResponse = await api.post('/properties/booking', {
              specialLotId: requestData.specialLotId,
              message: sanitizedRequest.requestMessage,
              contactPhone: sanitizedRequest.contactPhone,
              documentUrl: sanitizedRequest.documentUrl,
              documentOriginalName: sanitizedRequest.documentOriginalName,
              userId: sanitizedRequest.userId,
              agencyId: sanitizedRequest.agencyId // Include agencyId here too
            });
            console.log('Properties endpoint booking successful');
            return propertiesResponse.data;
          } catch (propertiesError) {
            console.warn('Properties endpoint also failed:', propertiesError.message);
          }
        }
        
        // 5. Final attempt with minimal data
        try {
          console.log('Making final attempt with minimal data');
          const minimalData = {
            lotId: sanitizedRequest.lotId,
            unitId: sanitizedRequest.unitId,
            specialLotId: sanitizedRequest.specialLotId,
            requestMessage: sanitizedRequest.requestMessage,
            documentOriginalName: sanitizedRequest.documentOriginalName,
            agencyId: sanitizedRequest.agencyId
          };
          
          const minimalResponse = await api.post('/bookings/request', minimalData);
          console.log('Minimal data request successful');
          return minimalResponse.data;
        } catch (minimalError) {
          console.warn('Minimal data attempt also failed:', minimalError.message);
        }
        
        // If all methods fail, use the error from the original endpoint
        throw lastError;
      }
    }
    
  } catch (error) {
    console.error('All booking request methods failed:', error.response?.data || error.message);
    
    // Store the failed request in localStorage for potential recovery
    try {
      const failedRequests = JSON.parse(localStorage.getItem('failedBookingRequests') || '[]');
      failedRequests.push({
        timestamp: Date.now(),
        requestData,
        error: {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        }
      });
      localStorage.setItem('failedBookingRequests', JSON.stringify(failedRequests));
      localStorage.setItem('bookingRecoveryNeeded', 'true');
      console.log('Failed booking request saved to localStorage for recovery');
    } catch (storageError) {
      console.warn('Failed to save request to localStorage:', storageError);
    }
    
    throw error;
  }
};

// New function specifically for compound property bookings
export const bookProperty = async (propertyData) => {
  try {
    console.log('Attempting to book property with compound ID:', propertyData);
    
    // Validate required fields
    if (!propertyData.propertyId && !propertyData.specialLotId) {
      throw new Error('Property ID is required');
    }
    
    // Use propertyId as the main identifier, fall back to specialLotId if needed
    const propertyId = propertyData.propertyId || propertyData.specialLotId;
    
    // Parse the compound ID to ensure we have all necessary information
    let specialLotType = null;
    let specialLotNumber = null;
    
    if (typeof propertyId === 'string' && propertyId.match(/^type-([a-z][0-9]?)-(\d+)$/i)) {
      const match = propertyId.match(/^type-([a-z][0-9]?)-(\d+)$/i);
      specialLotType = `Type ${match[1].toUpperCase()}`;
      specialLotNumber = match[2];
      console.log(`Parsed compound ID: Type=${specialLotType}, Number=${specialLotNumber}`);
    }
    
    // Create MongoDB-compatible synthetic ID for server that expects ObjectIDs
    // MongoDB ObjectIDs are 24 hex chars, so pad with zeros as needed
    let syntheticLotId = null;
    if (specialLotNumber) {
      // Create a predictable ID format that looks like a MongoDB ID but is derived from the lot number
      syntheticLotId = `5f${specialLotNumber.padStart(22, '0')}`;
      console.log(`Created synthetic lot ID: ${syntheticLotId}`);
    }
    
    // Get user data to include in the request
    const userJson = localStorage.getItem('user');
    let userData = null;
    try {
      if (userJson) {
        userData = JSON.parse(userJson);
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
    
    // Variable to track which endpoint we're using
    let endpointUsed = "";
    
    // Variable to store response across retries
    let response = null;
    
    // Start by trying the dedicated property endpoint
    try {
      console.log('Trying dedicated /api/bookings/property endpoint');
      
      // Set up retry logic for the property endpoint
      const maxRetries = 3;
      let attempt = 0;
      let lastError = null;
      
      while (attempt < maxRetries) {
        attempt += 1;
        console.log(`Property booking attempt ${attempt}/${maxRetries}`);
        
        try {
          response = await axios.post(
            `${API_URL}/bookings/property`,
            {
              propertyId,
              specialLotId: propertyId,
              specialLotType: specialLotType,
              specialLotNumber: specialLotNumber,
              lotId: syntheticLotId, // Include the synthetic lot ID
              requestMessage: propertyData.requestMessage || "",
              contactPhone: propertyData.contactPhone || "",
              documentUrl: propertyData.documentUrl || "",
              documentOriginalName: propertyData.documentOriginalName || '',
              userId: propertyData.userId || null,
              _debug: {
                userAgent: navigator.userAgent,
                path: window.location.pathname,
                timestamp: new Date().toISOString(),
                specialLot: {
                  propertyId,
                  specialLotType,
                  specialLotNumber,
                  syntheticLotId
                }
              }
            },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // If we get here, the request was successful
          console.log(`Property endpoint successful on attempt ${attempt}:`, response.data);
          endpointUsed = "property";
          return response.data;
        } catch (err) {
          lastError = err;
          console.error(`Attempt ${attempt} failed:`, err.message);
          
          // If this is a 404 error (endpoint not found), no need to retry - we'll try alternative endpoint
          if (err.response && err.response.status === 404) {
            console.log('Property endpoint not available (404), will try alternative endpoint');
            break;
          }
          
          // If we've reached max retries, break out to try alternative
          if (attempt >= maxRetries) {
            console.error(`Property booking endpoint failed after retries:`, err);
            console.error(`Property booking error details:`, err.response?.data || {});
            break;
          }
          
          // Wait before retrying - increasing delay with each attempt
          const delay = attempt * 1000;
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      // If we get here and haven't returned, the property endpoint failed
      // Try a direct axios call as a fallback
      console.log("Attempting direct axios call to property endpoint");
      
      attempt = 0;
      while (attempt < maxRetries) {
        attempt += 1;
        console.log(`Property booking attempt ${attempt}/${maxRetries}`);
        
        try {
          // Try with regular fetch API as a backup
          response = await axios({
            method: 'post',
            url: `${window.location.origin}/api/bookings/property`,
            data: {
              propertyId,
              requestMessage: propertyData.requestMessage || "",
              contactPhone: propertyData.contactPhone || "",
              documentUrl: propertyData.documentUrl || "",
              documentOriginalName: propertyData.documentOriginalName || '',
              userId: propertyData.userId || null
            },
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log(`Direct axios call successful on attempt ${attempt}:`, response.data);
          endpointUsed = "property-direct";
          return response.data;
        } catch (err) {
          console.error(`Attempt ${attempt} failed:`, err.message);
          
          // If this is a 404 error, no need to retry - we'll try alternative endpoint
          if (err.response && err.response.status === 404) {
            console.log('Direct property endpoint call also returned 404, will try alternative endpoint');
            break;
          }
          
          if (attempt >= maxRetries) {
            console.error(`Direct call also failed after retries:`, err);
            break;
          }
          
          const delay = attempt * 1000;
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    } catch (propertyError) {
      console.error('Error with property endpoint:', propertyError);
    }
    
    // If we get here, both property endpoint attempts failed
    // Try with alternative property format if we haven't already
    // The server might expect different payload formats depending on the endpoint
    console.log('Attempting with alternative property format');
    
    // Use the data we already parsed at the top of the function
    // We've already extracted specialLotType, specialLotNumber, and generated syntheticLotId
    
    // Try using the standard booking request endpoint as fallback
    try {
      // Create a more complete payload with our improved parsing
      const enhancedPayload = {
        lotId: syntheticLotId || null,
        specialLotId: propertyId,
        specialLotType: specialLotType,
        specialLotNumber: specialLotNumber,
        requestMessage: propertyData.requestMessage || "",
        contactPhone: propertyData.contactPhone || "",
        documentUrl: propertyData.documentUrl || "",
        documentOriginalName: propertyData.documentOriginalName || '',
        userId: propertyData.userId || null,
        _debug: {
          timestamp: new Date().toISOString(),
          fallback: true,
          enhancedData: true
        }
      };
      
      console.log('Trying with enhanced fallback format:', enhancedPayload);
      
      response = await axios.post(
        `${API_URL}/bookings/request`,
        enhancedPayload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Alternative format request successful:', response.data);
      endpointUsed = "request-fallback";
      return response.data;
    } catch (altFormatError) {
      console.error('Alternative format also failed:', altFormatError);
      
      // If all server-side attempts fail, create a client-side fallback booking
      // that will be synchronized later when connectivity is restored
      
      // Create a fallback booking object for the client
      const fallbackBooking = {
        _id: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        user: userData?._id || 'unknown',
        specialLotId: propertyId,
        specialLotType: specialLotType,
        specialLotNumber: specialLotNumber,
        status: "pending_sync",
        requestMessage: propertyData.requestMessage || "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creatorEmail: userData?.email || "unknown",
        creatorName: userData?.name || "Unknown",
        creatorPhone: propertyData.contactPhone || userData?.phone || "",
        documentUrl: propertyData.documentUrl || "",
        documentOriginalName: propertyData.documentOriginalName || '',
        _isClientFallback: true // Special flag to indicate this was created client-side
      };
      
      console.log('Created client-side fallback booking:', fallbackBooking);
      
      // Store the failed request for later recovery
      try {
        // Get existing failed requests
        const failedRequestsJSON = localStorage.getItem('failedBookingRequests');
        let failedRequests = [];
        if (failedRequestsJSON) {
          failedRequests = JSON.parse(failedRequestsJSON);
        }
        
        // Add this request
        failedRequests.push({
          timestamp: Date.now(),
          type: 'propertyBooking',
          requestData: {
            propertyId,
            specialLotId: propertyId,
            specialLotType: specialLotType,
            specialLotNumber: specialLotNumber,
            requestMessage: propertyData.requestMessage || "",
            contactPhone: propertyData.contactPhone || "",
            documentUrl: propertyData.documentUrl || "",
            documentOriginalName: propertyData.documentOriginalName || '',
            userId: propertyData.userId || null
          },
          fallbackBookingId: fallbackBooking._id
        });
        
        // Store back in localStorage
        localStorage.setItem('failedBookingRequests', JSON.stringify(failedRequests));
        localStorage.setItem('bookingRecoveryNeeded', 'true');
        console.log('Stored failed booking request for future recovery');
      } catch (storageError) {
        console.error('Error storing failed request:', storageError);
      }
      
      return fallbackBooking;
    }
  } catch (error) {
    console.error('Error in bookProperty:', error);
    throw error;
  }
};

export const getAgencyBookingRequests = async () => {
  try {
    const token = localStorage.getItem('token');
    const userJson = localStorage.getItem('user');
    let agencyId = null;
    
    if (userJson) {
      const user = JSON.parse(userJson);
      agencyId = user.agencyId;
    }
    
    if (!agencyId) {
      console.error('getAgencyBookingRequests called without agency ID in user data');
      return [];
    }
    
    console.log(`Fetching booking requests for agency ${agencyId}`);
    
    // Explicitly request only user-created bookings (status: pending_agent)
    const response = await api.get(`/bookings/agency/${agencyId}?createdBy=user`);
    
    // Additional client-side filtering to ensure only user requests are shown
    const bookings = response.data || [];
    
    // Debug the response
    console.log(`Raw booking requests data:`, bookings.slice(0, 2)); // Log first 2 bookings for debugging
    
    // Extra data processing to ensure we have all required fields with proper structure
    const processedBookings = bookings
      .filter(booking => booking.status === 'pending_agent')
      .map(booking => {
        // Ensure we have basic properties
        const processedBooking = {
          ...booking,
          // If property info is missing, ensure we have defaults
          lotType: booking.lotType || (booking.lot && booking.lot.type) || 'Property',
          lotNumber: booking.lotNumber || (booking.lot && booking.lot.lotNumber) || 'N/A',
          // If client info is missing, ensure we have defaults
          name: booking.name || (booking.user && booking.user.name) || 'Unknown Client',
          email: booking.email || (booking.user && booking.user.email) || 'N/A',
          phone: booking.phone || booking.contactPhone || (booking.user && booking.user.phone) || 'N/A'
        };
        return processedBooking;
      });
    
    console.log(`Retrieved ${processedBookings.length} filtered booking requests for agency ${agencyId}`);
    return processedBookings;
  } catch (error) {
    console.error('Error fetching agency booking requests:', error.response?.data || error.message);
    
    // Try alternate endpoint if the main one fails
    try {
      console.log('Trying alternate endpoint for booking requests');
      const token = localStorage.getItem('token');
      const userJson = localStorage.getItem('user');
      let agencyId = null;
      
      if (userJson) {
        const user = JSON.parse(userJson);
        agencyId = user.agencyId;
      }
      
      if (!agencyId) {
        return [];
      }
      
      // Try direct endpoint with axios
      const altResponse = await axios.get(
        `${window.location.origin}/api/bookings/agency-requests?createdBy=user`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const altBookings = altResponse.data || [];
      
      // Process the bookings to ensure we have all required fields
      const processedAltBookings = altBookings
        .filter(booking => booking.status === 'pending_agent' || booking.status === 'pending')
        .map(booking => {
          // Ensure we have basic properties
          return {
            ...booking,
            // If property info is missing, ensure we have defaults
            lotType: booking.lotType || (booking.lot && booking.lot.type) || 'Property',
            lotNumber: booking.lotNumber || (booking.lot && booking.lot.lotNumber) || 'N/A',
            // If client info is missing, ensure we have defaults
            name: booking.name || (booking.user && booking.user.name) || 'Unknown Client',
            email: booking.email || (booking.user && booking.user.email) || 'N/A',
            phone: booking.phone || booking.contactPhone || (booking.user && booking.user.phone) || 'N/A'
          };
        });
      
      console.log(`Retrieved ${processedAltBookings.length} booking requests from alternate endpoint`);
      return processedAltBookings;
    } catch (altError) {
      console.error('Alternate endpoint also failed:', altError.message);
      return [];
    }
  }
};

export const getAgencyUsers = async (agencyId) => {
  try {
    const response = await api.get(`/users/agency/${agencyId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Create a booking for a property
export const createBookingForProperty = async (bookingData) => {
  try {
    // Determine if this is a compound ID
    const isCompoundId = bookingData.propertyId && 
                         typeof bookingData.propertyId === 'string' && 
                         bookingData.propertyId.match(/^type-[a-z]-\d+$/i);
    
    if (isCompoundId) {
      // Use the property endpoint for compound IDs
      const response = await api.post('/bookings/property', bookingData);
      return response.data;
    } else {
      // Use the standard request endpoint for regular IDs
      const response = await api.post('/bookings/request', bookingData);
      return response.data;
    }
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getBookingRequests = async () => {
  try {
    // Get the current user's role to determine which endpoint to use
    const userJson = localStorage.getItem('user');
    let userData = null;
    if (userJson) {
      userData = JSON.parse(userJson);
    }
    
    // Add timestamp to prevent caching issues
    const timestamp = new Date().getTime();
    console.log('Fetching booking requests...');
    
    // For super_admin, use the admin endpoint
    if (userData && userData.role === 'super_admin') {
      console.log('Using admin endpoint for super_admin');
      try {
        const response = await api.get(`/bookings/admin?_t=${timestamp}`);
        console.log(`Found ${response.data.length} admin booking requests`);
        return response.data;
      } catch (error) {
        console.error('Admin endpoint failed:', error);
        // Try the all endpoint as fallback
        const allResponse = await api.get(`/bookings/all?_t=${timestamp}`);
        return allResponse.data.filter(b => 
          b.status === 'pending_admin' || 
          b.status === 'approved' || 
          b.status === 'rejected'
        );
      }
    }
    
    // For agent_admin, use the agency-requests endpoint
    if (userData && userData.role === 'agent_admin') {
      console.log('Using agency requests endpoint for agent_admin');
      try {
        const response = await api.get(`/bookings/agency-requests?_t=${timestamp}`);
        console.log(`Found ${response.data.length} agency booking requests`);
        return response.data;
      } catch (error) {
        console.error('Agency requests endpoint failed:', error);
        // Try the agent endpoint as fallback
        const agentResponse = await api.get(`/bookings/agent?_t=${timestamp}`);
        return agentResponse.data;
      }
    }
    
    // Default endpoint for regular users
    const response = await api.get(`/bookings/requests?_t=${timestamp}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking requests:', error.response?.data || error.message);
    
    // Try a direct fetch as last resort
    try {
      console.log('Trying direct axios call for bookings');
      const token = localStorage.getItem('token');
      const directResponse = await axios.get(
        `${window.location.origin}/api/bookings/all`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (directResponse.data && Array.isArray(directResponse.data)) {
        console.log(`Direct call returned ${directResponse.data.length} bookings`);
        return directResponse.data;
      }
    } catch (directError) {
      console.error('Direct call also failed:', directError);
    }
    
    throw error;
  }
};

export const respondToBookingRequest = async (bookingId, isApproved, message) => {
  try {
    // Handle both boolean and string status values
    let status;
    if (typeof isApproved === 'boolean') {
      status = isApproved ? 'approved' : 'rejected_by_agent';
    } else {
      // If it's already a string status, map it to a valid value
      // Map status values to ones the server accepts
      if (isApproved === 'booked') {
        status = 'approved';
      } else if (isApproved === 'sold') {
        // Keep as 'sold' which is a valid status in the database
        status = 'sold';
      } else {
        status = isApproved;
      }
    }
    
    console.log(`Sending booking response for ID ${bookingId} with status: ${status} (original: ${isApproved})`);
    
    // Include the message if provided
    const requestData = { status };
    if (message) {
      requestData.message = message;
    }
    
    // Add user info to help debugging permission issues
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        // Include user details to help server debug permissions
        requestData._debug = {
          userId: userData._id,
          userRole: userData.role,
          userAgencyId: userData.agencyId,
          timestamp: new Date().toISOString()
        };
      }
    } catch (err) {
      console.warn('Could not add debug info:', err);
    }
    
    // Create retry helper with exponential backoff
    const requestWithRetry = async (url, data, config, maxRetries = 3) => {
      let lastError = null;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          // Add attempt number to request data for debugging
          const requestWithAttempt = {
            ...data,
            _attempt: attempt + 1,
            _maxAttempts: maxRetries
          };
          
          console.log(`Attempt ${attempt + 1}/${maxRetries} for ${url}`);
          
          // Wait before retrying (except on first attempt)
          if (attempt > 0) {
            const delay = Math.min(1000 * Math.pow(2, attempt), 8000); // 2s, 4s, 8s
            console.log(`Waiting ${delay}ms before retry`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          
          return await api.put(url, requestWithAttempt, config);
        } catch (error) {
          lastError = error;
          console.warn(`Attempt ${attempt + 1} failed:`, error.message);
          
          // If it's a validation error or bad request, don't retry
          if (error.response && (error.response.status === 400 || error.response.status === 422)) {
            console.error("Validation error, not retrying:", error.response.data);
            throw error;
          }
        }
      }
      throw lastError; // All attempts failed
    };
    
    // Always try the direct-status endpoint first as our primary approach
    try {
      console.log("Sending request to status-direct endpoint with data:", requestData);
      const response = await requestWithRetry(`/bookings/${bookingId}/status-direct`, requestData);
      console.log("Status update successful via direct endpoint");
      return response.data;
    } catch (directError) {
      console.error("Error with direct update:", directError.message || directError);
      
      // If 404 (not found), the endpoint doesn't exist, try status-update endpoint
      if (directError.response && directError.response.status === 404) {
        console.log("Direct endpoint not found, trying status-update endpoint");
        try {
          const updateResponse = await requestWithRetry(`/bookings/${bookingId}/status-update`, requestData);
          console.log("Status update successful via status-update endpoint");
          return updateResponse.data;
        } catch (updateError) {
          console.error("Error with status-update endpoint:", updateError.message || updateError);
          
          // If both fail, try the standard endpoint as a last resort
          console.log("All alternatives failed, trying standard status endpoint");
          const standardResponse = await requestWithRetry(`/bookings/${bookingId}/status`, requestData);
          console.log("Status update successful via standard endpoint");
          return standardResponse.data;
        }
      }
      
      // For other errors, try axios directly with full headers
      try {
        console.log("Trying direct axios call with full headers");
        const token = localStorage.getItem('token');
        const fallbackResponse = await axios.put(
          `${window.location.origin}/api/bookings/${bookingId}/status-direct`,
          requestData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log("Status update successful via direct axios call");
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error("All endpoints failed:", fallbackError.message || fallbackError);
        
        // Store the failed status update in localStorage for recovery
        try {
          const failedUpdates = JSON.parse(localStorage.getItem('failedStatusUpdates') || '[]');
          failedUpdates.push({
            timestamp: Date.now(),
            bookingId,
            status,
            message,
            error: {
              message: directError.message,
              status: directError.response?.status,
              data: directError.response?.data
            }
          });
          localStorage.setItem('failedStatusUpdates', JSON.stringify(failedUpdates));
          console.log('Failed status update saved to localStorage for recovery');
        } catch (storageError) {
          console.warn('Failed to save update to localStorage:', storageError);
        }
        
        throw directError; // Throw the original error
      }
    }
  } catch (error) {
    console.error('Error responding to booking request:', error.response?.data || error.message || error);
    throw error;
  }
};

export const fetchUserBookings = async () => {
  try {
    const response = await api.get('/bookings/user');
    return response.data;
  } catch (error) {
    console.error('Error fetching user bookings:', error.response?.data || error.message);
    throw error;
  }
};

export const cancelBooking = async (bookingId) => {
  try {
    const response = await api.delete(`/bookings/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error(`Error cancelling booking ${bookingId}:`, error.response?.data || error.message);
    throw error;
  }
};

// Debug functions
export const debugUserBookings = async (userId) => {
  try {
    // This is a fallback function when the normal API fails
    // It attempts to get bookings for a specific user ID
    console.log(`Attempting debug fetch of bookings for user ${userId}`);
    
    // This would be a direct endpoint that takes a user ID parameter
    const response = await api.get(`/bookings/debug/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Debug bookings fetch failed:', error.response?.data || error.message);
    // If debug endpoint also fails, return empty array as fallback
    return [];
  }
};

// Agency requests service
export const getAgencyRequests = async (agencyId) => {
  try {
    if (!agencyId) {
      console.error('getAgencyRequests called without agencyId');
      return [];
    }
    
    console.log(`Attempting to fetch agency requests for agency ID: ${agencyId}`);
    
    // Try multiple endpoint formats for better compatibility
    let response;
    let endpointUsed;
    
    // First try the standard endpoint
    try {
      response = await api.get(`/agencies/${agencyId}/requests`);
      endpointUsed = 'standard';
    } catch (standardError) {
      console.warn('Standard endpoint failed:', standardError.message);
      
      // Try alternative endpoint
      try {
        response = await api.get(`/agency-requests/${agencyId}`);
        endpointUsed = 'alternative';
      } catch (altError) {
        console.warn('Alternative endpoint failed:', altError.message);
        
        // Last resort endpoint
        try {
          response = await api.get(`/membership-requests?agencyId=${agencyId}`);
          endpointUsed = 'membership';
        } catch (membershipError) {
          console.warn('All API endpoints failed');
          
          // If all API endpoints fail, use localStorage as fallback
          const localRequestsJson = localStorage.getItem('agencyRequests');
          if (localRequestsJson) {
            try {
              const localRequests = JSON.parse(localRequestsJson);
              if (Array.isArray(localRequests)) {
                // Filter for this agency's pending requests
                const agencyRequests = localRequests.filter(
                  req => req.agencyId === agencyId && req.status === 'pending'
                );
                
                console.log(`Found ${agencyRequests.length} requests in localStorage for agency ${agencyId}`);
                return agencyRequests;
              }
            } catch (parseError) {
              console.error('Error parsing localStorage agency requests:', parseError);
            }
          }
          
          return [];
        }
      }
    }
    
    // If we have a response, process it
    if (response && response.data) {
      console.log(`Successfully retrieved agency requests using ${endpointUsed} endpoint:`, response.data);
      
      // Make sure we're returning an array
      const requestsData = Array.isArray(response.data) ? response.data : 
                          (response.data.requests ? response.data.requests : []);
      
      // Store in localStorage for fallback
      try {
        localStorage.setItem('agencyRequests', JSON.stringify(requestsData));
      } catch (storageError) {
        console.warn('Failed to store agency requests in localStorage:', storageError);
      }
      
      return requestsData;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching agency requests:', error.response?.data || error.message);
    return [];
  }
};

export const getAgencyBookingHistory = async () => {
  try {
    const token = localStorage.getItem('token');
    const userJson = localStorage.getItem('user');
    let agencyId = null;
    
    if (userJson) {
      const user = JSON.parse(userJson);
      agencyId = user.agencyId;
    }
    
    if (!agencyId) {
      console.error('getAgencyBookingHistory called without agency ID in user data');
      return [];
    }
    
    // Add createdBy=agent parameter to only get agent-created bookings
    const response = await api.get(`/bookings/agency/${agencyId}/history?createdBy=agent`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agency booking history:', error.response?.data || error.message);
    return [];
  }
};

// Booking Request services
export const submitBookingRequest = async (formData, onUploadProgress) => {
  try {
    // Get token for authenticated requests
    const token = localStorage.getItem('token');
    
    // Get user data from localStorage
    const userJson = localStorage.getItem('user');
    let userData = null;
    
    if (userJson) {
      try {
        userData = JSON.parse(userJson);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    
    // Check if user is logged in
    if (!userData || !userData._id) {
      throw new Error('You must be logged in to submit a booking request.');
    }
    
    // Extract the lot information from formData.lot or individual fields
    const lotId = formData.lot?._id || formData.lot?.id || formData.lotId || formData.lot?.lotNumber || formData.lot;
    
    console.log('Lot identification debug:', {
      'formData.lot?._id': formData.lot?._id,
      'formData.lot?.id': formData.lot?.id,
      'formData.lotId': formData.lotId,
      'formData.lot?.lotNumber': formData.lot?.lotNumber,
      'formData.lot': formData.lot,
      'final lotId': lotId
    });
    
    if (!lotId) {
      throw new Error('Lot information is required for booking');
    }
    
    // Basic required fields
    const bookingData = {
      userId: userData._id,
      lotId: lotId,
      requestMessage: formData.description || formData.requestMessage || 'Booking request',
      // Use the status from formData if provided, otherwise default to pending_agent
      status: formData.status || 'pending_agent',
      contactPhone: formData.phone || formData.contactPhone || userData.phone || '',
      name: formData.name || userData.name || '',
      email: formData.email || userData.email || '',
      documentUrl: formData.documentUrl || '',
      documentOriginalName: formData.documentOriginalName || formData.documentName || '',
    };
    
    // Include agency if available
    if (userData.agencyId) {
      bookingData.agencyId = userData.agencyId;
    }
    
    // Include lot details if available
    if (formData.lot?.lotNumber || formData.lotNumber) bookingData.lotNumber = formData.lot?.lotNumber || formData.lotNumber;
    if (formData.lot?.type || formData.lotType) bookingData.lotType = formData.lot?.type || formData.lotType;
    
    console.log('Submitting booking request with data:', {
      ...bookingData,
      hasDocument: !!bookingData.documentUrl,
      hasDocumentName: !!bookingData.documentOriginalName
    });
    
    // Use the correct endpoint based on user role
    const endpoint = userData.role === 'agent_admin' ? '/bookings' : '/bookings/request';
    
    const response = await axios.post(
      `${API_URL}${endpoint}`,
      bookingData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Booking request submitted successfully to ${endpoint}:`, response.data);
    return response;
  } catch (error) {
    console.error('Error submitting booking request:', error);
    
    // Log detailed error information
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error details:', error.response.data);
    }
    
    throw error;
  }
};

export const getBookingRequestById = async (requestId) => {
  try {
    const response = await api.get(`/booking-requests/${requestId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking request details:', error.response?.data || error.message);
    throw error;
  }
};

export default api;

// Add this function to check agency membership status
export const checkAgencyMembershipStatus = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await axios.get('/api/users/me', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  // Update the user data in localStorage with the latest from server
  if (response.data) {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = { 
      ...currentUser, 
      agencyId: response.data.agencyId,
      agencyStatus: response.data.agencyStatus,
      agencyName: response.data.agencyName,
      position: response.data.position
    };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }

  return response.data;
};

// Upload document helper function
export const uploadDocument = async (file, type, bookingId = null) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', type); // Changed from 'type' to 'documentType' to match server
    
    // If bookingId is provided, include it
    if (bookingId) {
      formData.append('bookingId', bookingId);
    }
    
    const response = await api.post('/uploads/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data.documentUrl || response.data.url;
  } catch (error) {
    console.error(`Error uploading ${type} document:`, error);
    throw error;
  }
};

// Add new function for fetching properties with better error handling
export const fetchProperties = async (params = {}) => {
  try {
    console.log('Attempting to fetch properties with params:', params);
    
    // Try multiple endpoints with fallbacks
    const endpoints = [
      '/public/properties',
      '/properties',
      '/properties/public',
      '/lots/properties'
    ];
    
    let lastError = null;
    
    // Try each endpoint
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const queryParams = new URLSearchParams({
          ...params,
          _t: timestamp
        }).toString();
        
        const response = await axios.get(`${API_URL}${endpoint}?${queryParams}`, {
          timeout: 10000, // 10 second timeout
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.data) {
          console.log(`Successfully fetched properties from ${endpoint}`);
          return response.data;
        }
      } catch (error) {
        console.warn(`Endpoint ${endpoint} failed:`, error.message);
        lastError = error;
        
        // If it's not a 502 error, try the next endpoint
        if (error.response?.status !== 502) {
          continue;
        }
        
        // For 502 errors, try the same endpoint with the current origin
        try {
          console.log(`Trying ${endpoint} with current origin fallback`);
          const fallbackResponse = await axios.get(
            `${window.location.origin}/api${endpoint}?${new URLSearchParams(params).toString()}`,
            {
              timeout: 15000, // Longer timeout for fallback
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            }
          );
          
          if (fallbackResponse.data) {
            console.log(`Successfully fetched properties from origin fallback`);
            return fallbackResponse.data;
          }
        } catch (fallbackError) {
          console.warn(`Origin fallback also failed:`, fallbackError.message);
        }
      }
    }
    
    // If all endpoints fail, throw the last error
    throw lastError || new Error('All property endpoints failed');
  } catch (error) {
    console.error('Error fetching properties:', error.response?.data || error.message);
    throw error;
  }
};

// Helper function to check if the current token is likely expired
export const checkTokenValidity = () => {
  const token = localStorage.getItem('token');
  const tokenTimestamp = localStorage.getItem('tokenTimestamp');
  
  if (!token) {
    return { valid: false, reason: 'No token found' };
  }
  
  if (!tokenTimestamp) {
    return { valid: false, reason: 'No token timestamp found' };
  }
  
  const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours
  
  if (tokenAge > maxAge) {
    return { valid: false, reason: 'Token appears to be expired', age: Math.round(tokenAge / (60 * 60 * 1000)) + ' hours old' };
  }
  
  return { valid: true, age: Math.round(tokenAge / (60 * 60 * 1000)) + ' hours old' };
};

// Helper function to clear expired tokens and redirect to login
export const handleExpiredToken = (reason = 'Token expired') => {
  console.warn('Handling expired token:', reason);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tokenTimestamp');
  localStorage.removeItem('lastActivityTime');
  
  // Store the current page for redirect after login
  if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    window.location.href = '/login?reason=expired&message=' + encodeURIComponent(reason);
  }
};

// Debug function to check and fix token issues
export const debugAndFixTokenIssues = () => {
  console.log('=== TOKEN DEBUG INFORMATION ===');
  
  const token = localStorage.getItem('token');
  const tokenTimestamp = localStorage.getItem('tokenTimestamp');
  const user = localStorage.getItem('user');
  
  console.log('Token exists:', !!token);
  console.log('Token timestamp exists:', !!tokenTimestamp);
  console.log('User data exists:', !!user);
  
  if (token) {
    // Basic token structure check
    const tokenParts = token.split('.');
    console.log('Token has correct JWT structure:', tokenParts.length === 3);
    
    if (tokenParts.length === 3) {
      try {
        // Decode the payload (this is safe as it's just base64)
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Token payload:', {
          userId: payload.id || payload.userId,
          role: payload.role,
          exp: payload.exp,
          iat: payload.iat,
          expiresAt: new Date(payload.exp * 1000).toISOString(),
          issuedAt: new Date(payload.iat * 1000).toISOString(),
          isExpired: payload.exp * 1000 < Date.now()
        });
        
        // Check if token is expired
        if (payload.exp * 1000 < Date.now()) {
          console.error('❌ TOKEN IS EXPIRED!');
          console.log('Clearing expired token and redirecting to login...');
          handleExpiredToken('Token has expired and needs to be refreshed');
          return false;
        } else {
          console.log('✅ Token is still valid');
        }
      } catch (e) {
        console.error('❌ Error decoding token payload:', e.message);
        console.log('Clearing malformed token...');
        handleExpiredToken('Token is malformed and needs to be refreshed');
        return false;
      }
    }
  } else {
    console.error('❌ No token found in localStorage');
    console.log('Redirecting to login...');
    window.location.href = '/login?reason=no-token';
    return false;
  }
  
  if (tokenTimestamp) {
    const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
    const ageHours = Math.round(tokenAge / (60 * 60 * 1000));
    console.log('Token age:', ageHours + ' hours');
    
    if (ageHours > 24) {
      console.warn('⚠️ Token is older than 24 hours, may be expired');
    }
  }
  
  if (user) {
    try {
      const userData = JSON.parse(user);
      console.log('User data:', {
        id: userData._id,
        role: userData.role,
        email: userData.email,
        agencyId: userData.agencyId
      });
    } catch (e) {
      console.error('❌ Error parsing user data:', e.message);
    }
  }
  
  console.log('=== END TOKEN DEBUG ===');
  return true;
};

// Add this function to the global window object for easy access in console
if (typeof window !== 'undefined') {
  window.debugTokens = debugAndFixTokenIssues;
  window.clearExpiredTokens = () => handleExpiredToken('Manual token clear requested');
}
